import type { ReactNode } from "react";
import { Link } from "react-router";

import { cn } from "@/lib/cn";

/**
 * A row whose whole surface navigates, with an action area that does not.
 *
 * The product reference has a defect where clicking a row action also triggers
 * the row's navigation. The fix here is structural rather than defensive:
 *
 *  - Navigation is a real `<a>` stretched across the row by a pseudo-element,
 *    not an `onClick` on the container. Keyboard users get a genuine link.
 *  - Actions are painted above that stretched area, so the browser hit-tests
 *    the action, never the link underneath it.
 *  - `DataRow` exposes no row-level click handler at all, so there is nothing
 *    for an action's click to bubble into. That is the guarantee — not a
 *    `stopPropagation` call that a later refactor could drop.
 *
 * `to` must be an opaque project path. ADR 0001 forbids patient or candidate
 * identifiers in any URL; src/invariants enforces that for the route table.
 */
export function DataRow({
  to,
  title,
  titleAdornment,
  subtitle,
  columns,
  actions,
  className,
}: {
  to: string;
  title: ReactNode;
  /**
   * Non-interactive content beside the title — a status flag, typically.
   *
   * A sibling of the link rather than a child of it: folding it into the link
   * would make the link announce itself as the destination *plus* the flag,
   * which describes the destination wrongly. It sits under the stretched
   * navigation area, which is correct, because nothing here is clickable.
   */
  titleAdornment?: ReactNode;
  subtitle?: ReactNode;
  /** Trailing cells, rendered in order after the title block. */
  columns?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "relative flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-border px-5 py-4 last:border-b-0",
        "transition-colors hover:bg-secondary/60 focus-within:bg-secondary/60",
        className,
      )}
    >
      <div className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <Link
            to={to}
            className="text-sm font-semibold text-foreground after:absolute after:inset-0 after:content-[''] focus-visible:outline-none"
          >
            {title}
          </Link>
          {titleAdornment}
        </span>
        {subtitle ? (
          <p className="mt-0.5 text-sm text-muted-foreground">{subtitle}</p>
        ) : null}
      </div>

      {columns}

      {actions ? (
        <div className="relative z-10 flex shrink-0 items-center gap-1">
          {actions}
        </div>
      ) : null}
    </div>
  );
}
